import lozad from 'lozad';
import Clipboard from 'vue-clipboard2';
import Events from './../services/Events';

import * as Sentry from '@sentry/browser';
import { Vue as VueIntegration } from '@sentry/integrations';

import { currency } from '../filters/currency';

import CartStore from './store/modules/cart';
import CustomerStore from './store/modules/customer';
import NavigationStore from './store/modules/navigation';

import ScrollDirective from '../directives/scroll';
import ClickOutsideDirective from '../directives/click-outside';
import HeightFillScreen from '../directives/height-fill-screen';

import Lego from '../services/Lego';
import Heap from './../services/Heap';
import Zaius from './../services/Zaius';
import Klarna from './../services/Klarna';
import Katapult from './../services/Katapult';
import Talkable from './../services/Talkable';
import Kustomer from './../services/Kustomer';
import DataLayer from './../services/DataLayer';
import Mulberry from '../services/Mulberry';

export default {
    install(Vue, store) {
        registerSentryErrorTracking(Vue);
        registerPlugins(Vue);
        registerVuexStoreModules(store);
        registerModals(Vue);
        registerEvents(Vue);
        registerDirectives(Vue);
        registerFilters(Vue);
        registerMixins(Vue);
        registerComponents(Vue);
        setCsrfToken(Vue);
        setLegoId(Vue);
        setSettings(Vue);
        registerServices(Vue);
    },
};

function registerSentryErrorTracking(Vue) {
    if (process.env.MIX_ERROR_TRACKING_ENABLED) {
        Sentry.init({
            dsn: process.env.MIX_SENTRY_DSN_PUBLIC,
            integrations: [new VueIntegration({ Vue, attachProps: true })],
            release: Settings.projectId + '@' + Settings.version,
        });
    }
}

function registerPlugins(Vue) {
    Vue.use(Clipboard);
}

function registerVuexStoreModules(store) {
    if (!store) {
        throw new Error('Please provide a Vuex store');
    } else {
        store.registerModule('cart', CartStore);
        store.registerModule('customer', CustomerStore);
        store.registerModule('navigation', NavigationStore);
    }
}

function registerModals(Vue) {
    const Events = new Vue();
    const modal = new Vue({
        methods: {
            open(name, payload) {
                Events.$emit(`open:${name}`, payload);
            },

            close(name) {
                Events.$emit(`close:${name}`);
            },

            on(event, callback) {
                Events.$on(event, callback);
            },
        },
    });

    // Extend `Vue.prototype` to include our global event bus.
    Object.defineProperty(Vue.prototype, '$modal', {
        get() {
            return modal;
        },
    });
}

function registerEvents(Vue) {
    // Extend `Vue.prototype` to include our global event bus.
    Object.defineProperty(Vue.prototype, '$events', {
        get() {
            return Events;
        },
    });
}

function registerDirectives(Vue) {
    Vue.directive('click-outside', ClickOutsideDirective);
    Vue.directive('scroll', ScrollDirective);
    Vue.directive('height-fill-screen', HeightFillScreen);
}

function registerFilters(Vue) {
    Vue.filter('currency', currency);
}

function registerComponents(Vue) {
    Vue.component('svg-icon', require('../common/components/SvgIcon').default);

    Vue.component(/* webpackPrefetch: false */ 'renderless-cart', () =>
        import('./components/RenderlessCart' /* webpackChunkName: "js/renderless-cart" */)
    );
    Vue.component(/* webpackPrefetch: false */ 'cart', () =>
        import('./components/Cart' /* webpackChunkName: "js/cart" */)
    );

    Vue.component(/* webpackPrefetch: false */ 'email-capture', () =>
        import('./components/EmailCapture' /* webpackChunkName: "js/email-capture" */)
    );

    Vue.component(/* webpackPrefetch: false */ 'carousel', () =>
        import('./components/Carousel/Carousel' /* webpackChunkName: "js/carousel" */)
    );
    Vue.component(/* webpackPrefetch: false */ 'slide', () =>
        import('./components/Carousel/Slide' /* webpackChunkName: "js/slide" */)
    );
    Vue.component(/* webpackPrefetch: false */ 'thumbnail', () =>
        import('./components/Carousel/Thumbnail' /* webpackChunkName: "js/thumbnail" */)
    );

    Vue.component(/* webpackPrefetch: false */ 'lazy-video', () =>
        import('./components/LazyVideo' /* webpackChunkName: "js/lazy-video" */)
    );
    Vue.component(/* webpackPrefetch: false */ 'lazy-img', () =>
        import('./components/LazyImg' /* webpackChunkName: "js/lazy-img" */)
    );

    Vue.component(/* webpackPrefetch: false */ 'product', () =>
        import('./components/Product/Product' /* webpackChunkName: "js/product" */)
    );
    Vue.component(/* webpackPrefetch: false */ 'upsell-product', () =>
        import('./components/Product/UpsellProduct' /* webpackChunkName: "js/upsell-product" */)
    );

    Vue.component(/* webpackPrefetch: false */ 'modal-fullscreen', () =>
        import('./components/Modal/ModalFullscreen' /* webpackChunkName: "js/modal-fullscreen" */)
    );
    Vue.component(/* webpackPrefetch: false */ 'modal-dialog', () =>
        import('./components/Modal/ModalDialog' /* webpackChunkName: "js/modal-dialog" */)
    );

    Vue.component(/* webpackPrefetch: false */ 'copy-to-clipboard', () =>
        import('../common/components/CopyToClipboard' /* webpackChunkName: "js/copy-to-clipboard" */)
    );

    Vue.component(/* webpackPrefetch: false */ 'auto-apply-coupon-code', () =>
        import('./components/AutoApplyCouponCode' /* webpackChunkName: "js/auto-apply-coupon-code" */)
    );

    Vue.component(/* webpackPrefetch: false */ 'promobar-email-capture', () =>
        import('./components/RenderlessPromobarEmailCapture' /* webpackChunkName: "js/promobar-email-capture" */)
    );

    Vue.component(/* webpackPrefetch: false */ 'calculate-discount', () =>
        import('./components/Discounts/CalculateDiscount' /* webpackChunkName: "js/calculate-discount" */)
    );
    Vue.component(/* webpackPrefetch: false */ 'tiered-discount-dollar-amount', () =>
        import('./components/Discounts/TieredDollarAmount' /* webpackChunkName: "js/tiered-discount-dollar-amount" */)
    );

    Vue.component(/* webpackPrefetch: false */ 'heading', () =>
        import('./components/Heading' /* webpackChunkName: "js/heading" */)
    );

    // Vue.component(/* webpackPrefetch: false */ 'text-locale-selector', () => import('./components/Locale/TextLocaleSelector' /* webpackChunkName: "js/text-locale-selector" */));
    // Vue.component(/* webpackPrefetch: false */ 'text-currency-selector', () => import('./components/Currency/TextCurrencySelector' /* webpackChunkName: "js/text-currency-selector" */));
}

function setCsrfToken(Vue) {
    let token = document.head.querySelector('meta[name="csrf-token"]');

    if (!token) {
        console.error(
            'CSRF token not found: https://docs.hlx.dev/lego/store-front/layouts#csrf-token'
        );
    }

    Object.defineProperty(Vue.prototype, '$csrfToken', {
        get() {
            return token ? token.content : '';
        },
    });
}

function setLegoId(Vue) {
    let id = document.head.querySelector('meta[name="lego-id"]');

    if (!id) {
        console.error('Lego ID not found: https://docs.hlx.dev/lego/store-front/layouts#lego-id');
    }

    Object.defineProperty(Vue.prototype, '$legoId', {
        get() {
            return id ? id.content : '';
        },
    });
}

function setSettings(Vue) {
    if (!Settings) {
        console.error('Settings not found');
    }

    Object.defineProperty(Vue.prototype, '$settings', {
        get() {
            return Settings;
        },
    });
}

function registerMixins(Vue) {
    let currency = document.head.querySelector('meta[name="currency"]');
    let locale = document.head.querySelector('meta[name="locale"]');

    if (!currency) {
        console.error('Currency not found: https://docs.hlx.dev/lego/store-front/layouts#currency');
    }

    if (!locale) {
        console.error('Locale not found: https://docs.hlx.dev/lego/store-front/layouts#locale');
    }

    Vue.mixin({
        data() {
            return {
                _currency: currency ? currency.content : '',
                _locale: locale ? locale.content : '',
            };
        },

        mounted() {
            const observer = lozad();
            observer.observe();
        },

        methods: {
            scrollDown(y) {
                window.scroll({
                    top: y,
                    left: 0,
                    behavior: 'smooth',
                });
            },

            scrollToId(id, padding = 0) {
                let heightToElement = document.getElementById(id).offsetTop;
                let heightOfNav = document.getElementsByTagName('nav')[0].getBoundingClientRect()
                    .height;

                window.scrollTo({
                    top: heightToElement - heightOfNav - padding,
                    behavior: 'smooth',
                });
            },
        },
    });
}

function registerServices(Vue) {
    Object.defineProperty(Vue.prototype, '$services', {
        get() {
            return {
                dataLayer: DataLayer,
                chat: Kustomer,
                heap: Heap,
                katapult: Katapult,
                klarna: Klarna,
                referral: Talkable,
                zaius: Zaius,
                mulberry: Mulberry,
                sentry: Sentry,
            };
        },
    });

    window['lego'] = Lego;
}
